import { useMemo, useState } from "react";
import { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, Wrench, BarChart3, Download, FileSpreadsheet, Calendar } from "lucide-react";

const STATUSES = [
  { value: "active",   label: "Active only" },
  { value: "inactive", label: "Inactive only" },
  { value: "all",      label: "Active + inactive" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const startOfMonth = (iso) => { const d = new Date(iso); d.setDate(1); return d.toISOString().slice(0,10); };
const endOfMonth   = (iso) => { const d = new Date(iso); d.setMonth(d.getMonth()+1); d.setDate(0); return d.toISOString().slice(0,10); };
const subMonths    = (iso, n) => { const d = new Date(iso); d.setMonth(d.getMonth()-n); return d.toISOString().slice(0,10); };

function ListCard({ title, hint, icon: Icon, pdfHref, xlsxHref, status, onStatus, testidPrefix }) {
  return (
    <Card data-testid={`${testidPrefix}-card`} className="rounded-sm border border-slate-200 shadow-none p-5 flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 inline-flex items-center justify-center rounded-sm bg-slate-900 text-white">
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{hint}</div>
        </div>
      </div>
      <div>
        <Label className="text-xs text-slate-600">Show</Label>
        <Select value={status} onValueChange={onStatus}>
          <SelectTrigger data-testid={`${testidPrefix}-status`} className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button asChild variant="outline" data-testid={`${testidPrefix}-pdf`} className="rounded-sm border-slate-300">
          <a href={pdfHref(status)} download rel="noopener">
            <Download className="w-4 h-4 mr-1.5" /> PDF
          </a>
        </Button>
        <Button asChild data-testid={`${testidPrefix}-xlsx`} className="rounded-sm bg-slate-900 hover:bg-slate-800">
          <a href={xlsxHref(status)} download rel="noopener">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
          </a>
        </Button>
      </div>
    </Card>
  );
}

export default function ReportVehicles() {
  const [vehStatus, setVehStatus] = useState("active");
  const [supStatus, setSupStatus] = useState("active");

  // Maintenance summary range
  const [presetRange, setPresetRange] = useState("last_month");
  const [customFrom, setCustomFrom] = useState(startOfMonth(todayISO()));
  const [customTo, setCustomTo]     = useState(todayISO());

  const range = useMemo(() => {
    const t = todayISO();
    if (presetRange === "this_month")  return { from: startOfMonth(t), to: endOfMonth(t) };
    if (presetRange === "last_month")  { const lm = subMonths(t, 1); return { from: startOfMonth(lm), to: endOfMonth(lm) }; }
    if (presetRange === "last_3m")     return { from: subMonths(t, 3), to: t };
    if (presetRange === "last_6m")     return { from: subMonths(t, 6), to: t };
    if (presetRange === "last_12m")    return { from: subMonths(t, 12), to: t };
    if (presetRange === "ytd")         { const d = new Date(); return { from: `${d.getFullYear()}-01-01`, to: t }; }
    return { from: customFrom, to: customTo };
  }, [presetRange, customFrom, customTo]);

  const summaryQ = `?date_from=${range.from}&date_to=${range.to}`;

  return (
    <div data-testid="report-vehicles-page" className="space-y-10">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Reporting · Fleet</div>
        <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">D4PS Vehicle reports</h1>
        <p className="text-sm text-slate-600 mt-1">Download active or inactive fleet & maintenance supplier lists, and run maintenance summary reports for any period.</p>
      </div>

      <section data-testid="vehicle-lists">
        <div className="mb-3">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Lists</div>
          <h2 className="font-display text-2xl tracking-tight font-semibold text-slate-900 mt-1">Fleet & supplier lists</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ListCard
            title="Vehicles"
            hint="Your fleet, with road tax / insurance / MOT renewal dates."
            icon={Truck}
            status={vehStatus}
            onStatus={setVehStatus}
            testidPrefix="list-vehicles"
            pdfHref={(s) => fileUrl(`/reports/lists/vehicles.pdf?active=${s}`)}
            xlsxHref={(s) => fileUrl(`/reports/lists/vehicles.xlsx?active=${s}`)}
          />
          <ListCard
            title="Maintenance suppliers"
            hint="Garages & service providers, with vehicle types they handle."
            icon={Wrench}
            status={supStatus}
            onStatus={setSupStatus}
            testidPrefix="list-msup"
            pdfHref={(s) => fileUrl(`/reports/lists/maintenance-suppliers.pdf?active=${s}`)}
            xlsxHref={(s) => fileUrl(`/reports/lists/maintenance-suppliers.xlsx?active=${s}`)}
          />
        </div>
      </section>

      <section data-testid="maintenance-summary-section">
        <div className="mb-3">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Maintenance summary</div>
          <h2 className="font-display text-2xl tracking-tight font-semibold text-slate-900 mt-1">How much maintenance, when?</h2>
          <p className="text-sm text-slate-600 mt-1">Pick a period and download a summary showing jobs per month, breakdown by type (service / MOT / tyres etc) and spend per vehicle.</p>
        </div>

        <Card data-testid="maint-summary-card" className="rounded-sm border border-slate-200 shadow-none p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-xs text-slate-600">Period</Label>
              <Select value={presetRange} onValueChange={setPresetRange}>
                <SelectTrigger data-testid="maint-range-preset" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">This month</SelectItem>
                  <SelectItem value="last_month">Last month</SelectItem>
                  <SelectItem value="last_3m">Last 3 months</SelectItem>
                  <SelectItem value="last_6m">Last 6 months</SelectItem>
                  <SelectItem value="last_12m">Last 12 months</SelectItem>
                  <SelectItem value="ytd">Year to date</SelectItem>
                  <SelectItem value="custom">Custom range</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {presetRange === "custom" ? (
              <>
                <div>
                  <Label className="text-xs text-slate-600">From</Label>
                  <Input data-testid="maint-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">To</Label>
                  <Input data-testid="maint-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="mt-1 rounded-sm border-slate-300" />
                </div>
              </>
            ) : (
              <div className="md:col-span-2 text-xs text-slate-500 inline-flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" /> Covering: <span className="font-medium tabular-nums">{range.from} → {range.to}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Button asChild variant="outline" data-testid="maint-summary-pdf" className="rounded-sm border-slate-300">
                <a href={fileUrl(`/reports/maintenance-summary.pdf${summaryQ}`)} download rel="noopener">
                  <Download className="w-4 h-4 mr-1.5" /> PDF
                </a>
              </Button>
              <Button asChild data-testid="maint-summary-xlsx" className="rounded-sm bg-slate-900 hover:bg-slate-800">
                <a href={fileUrl(`/reports/maintenance-summary.xlsx${summaryQ}`)} download rel="noopener">
                  <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Excel
                </a>
              </Button>
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-slate-200 text-xs text-slate-500 inline-flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" /> Excel exports include 3 sheets: <span className="font-medium">Summary (by month)</span>, <span className="font-medium">By type</span>, <span className="font-medium">By vehicle</span>.
          </div>
        </Card>
      </section>
    </div>
  );
}
